
import React, { useState } from 'react'
import Paragraph from './Paragraph'
import Heading from './Heading'
import Button from './Button'
import Text from './Text'
import Box, { HBox } from './Box'
import Tabs from './Tabs'

/**
 * Intervals available.
 */

const intervals = ['month', 'year']

/**
 * Subscriptions component.
 */

export default function Subscriptions({ team, products, subscriptions, pendingPlanId, onApplyCoupon, onSubscribe, onCancel }) {
  const [interval, setInterval] = useState({ index: 0 })
  const team_id = team.id

  function content({ selected }) {
    return products.map(p => {
      const subscription = subscriptions.find(s => s.plan.product_id == p.id)
      return <Product
        key={p.id}
        interval={intervals[selected]}
        subscription={subscription}
        pendingPlanId={pendingPlanId}
        onApplyCoupon={e => onApplyCoupon({ team_id: team.id, ...e })}
        onSubscribe={e => onSubscribe({ team_id: team.id, ...e })}
        onCancel={e => onCancel({ team_id: team.id, ...e })}
        {...p} />
    })
  }

  return <Box>
    <Tabs
      labels={['Billed monthly', 'Billed annually']}
      selected={interval.index}
      content={content}
      onSelect={setInterval} />
  </Box>
}

/**
 * Product component.
 */

function Product({ id, name, plans, interval, subscription, pendingPlanId, onApplyCoupon, onSubscribe, onCancel }) {
  const filtered = plans
    .sort(byAmount)
    .filter(byInterval(interval))
    .filter(byActive(subscription))
  
  const legacy = filtered.length && subscription && subscription.plan.active == false
    ? <Plan
      key="legacy"
      isSubscribed
      onCancel={_ => onCancel({ subscription_id: subscription.id })}
      {...subscription.plan} />
    : null

  function content() {
    if (filtered.length == 0) {
      return <Paragraph muted>No {formatInterval(interval)} plans available.</Paragraph>
    }

    return <Box border borderRadius={2}>
      {legacy}
      {filtered.map((p, i) => {
        const last = i == filtered.length - 1

        const subscribe = _ => subscription
          ? onSubscribe({ plan_id: p.id, subscription_id: subscription.id })
          : onSubscribe({ plan_id: p.id })

        const applyCoupon = _ => onApplyCoupon({ plan_id: p.id, subscription_id: subscription.id })

        const cancel = _ => onCancel({ plan_id: p.id, subscription_id: subscription.id })

        const isSubscribed = subscription && subscription.plan.id == p.id
        const coupon = isSubscribed && subscription.coupon.name

        return <Plan
          key={p.id}
          pending={pendingPlanId == p.id}
          isSubscribed={isSubscribed}
          couponName={coupon}
          onApplyCoupon={applyCoupon}
          onSubscribe={subscribe}
          onCancel={cancel}
          isLast={last}
          {...p} />
      })}
    </Box>
  }
  
  return <Box marginTop={4}>
    <Heading size={2} marginTop={6} marginBottom={4}>{name}</Heading>
    {content()}
    {legacy && <Legacy />}
  </Box>
}

/**
 * Plan component.
 */

function Plan({ id, name, currency, interval, amount, usage_type, pending, isSubscribed, isLast, isLegacy, couponName, onApplyCoupon, onSubscribe, onCancel }) {
  if (interval == 'year') {
    amount = amount / 12 | 0
  }

  const coupon = couponName
    ? <Text>Discount: {couponName}</Text>
    : <CouponButton onClick={onApplyCoupon} />

  const button = isSubscribed
    ? <HBox alignY="center" gap={2}>
        {coupon}
        <CancelButton pending={pending} onClick={onCancel} />
      </HBox>
    : <SubscribeButton pending={pending} onClick={onSubscribe} />

  return <HBox
    displayName="Plan"
    alignY="center"
    alignX="between"
    paddingX={4}
    paddingY={3}
    borderBottom={!isLast}
    flex
  >
    <Heading size={200} minWidth={50}>{name}</Heading>
    <Amount
      amount={amount}
      currency={currency}
      usage_type={usage_type} />
    {button}
  </HBox>
}

/**
 * CouponButton component.
 */

function CouponButton(props) {
  return <Button {...props}>
    Apply coupon
  </Button>
}

/**
 * CancelButton component.
 */

function CancelButton(props) {
  return <Button
    theme="danger"
    height={32}
    width={115}
    justifyContent="center"
    {...props}
  >
    Cancel
  </Button>
}

/**
 * SubscribeButton component.
 */

function SubscribeButton(props) {
  return <Button
    theme="primary"
    height={32}
    width={115}
    justifyContent="center"
    {...props}
  >
    Subscribe
  </Button>
}

/**
 * Amount component.
 */

function Amount({ amount, currency, usage_type }) {
  // TODO: don't hard-code formatting, add Stripe plan meta-data
  const suffix = usage_type == 'metered'
    ? '/ GB'
    : '/ mo'

  const cost = usage_type == 'metered'
    ? amount * 1000
    : amount

  return <HBox alignY="center" flex>
    <Paragraph marginRight={1}>${cost.toFixed(2)}</Paragraph>
    <Paragraph textTransform="uppercase" muted> {currency} {suffix}</Paragraph>
  </HBox>
}

/**
 * Legacy component.
 */

function Legacy() {
  return <Paragraph marginTop={2} muted>
    The plan you're currently subscribed to is no longer offered.
  </Paragraph>
}

/**
 * formatInterval returns a formatted interval.
 */

function formatInterval(interval) {
  return {
    month: 'monthly',
    year: 'annual'
  }[interval]
}

/**
 * byActive filters active or legacy plans.
 */

function byActive(subscription) {
  return p => {
    if (p.active) return true
    return subscription && subscription.plan.id == p.id
  }
}

/**
 * byInterval filters plans by the interval.
 */

function byInterval(interval) {
  return p => p.interval == interval
}

/**
 * byAmount sorts by the amount ascending.
 */

function byAmount(a, b) {
  return a.amount - b.amount
}